// Imports
import { faPencil, faPlus } from '@fortawesome/pro-solid-svg-icons';
import React, { useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import styled from 'styled-components/macro';
import { fieldContainerBottomMargin } from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import File from '../../../../../../shareables/foundation/front-end/components/forms/fields/file';
import commonActions from '../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import { GetMyPhotosResponse } from '../../../../../../shareables/types/api/main/website/page/type/206/my-photos/get';
import { PostMyPhotosFiles } from '../../../../../../shareables/types/api/main/website/page/type/206/my-photos/post';
import { visuallyHiddenLabelStyle } from '../../../../shared-components/visually-hidden-label';
import Panel from '../shared-components/panel';
import UserControlButton from '../shared-components/user-control-button';
import PhotoGalleryTable from './table';


// Styled components
const ButtonGroup = styled.div`
	display: flex;
	gap: 0.3rem;
	flex-wrap: wrap;
	
	& > *:not(:last-child) {
		margin-bottom: 0;
	}
	
	.fa-pencil {
		padding-right: 0.25rem;
	}
`;

const FileFieldContainer = styled.div`
	&:not(:last-child) {
		margin-bottom: ${fieldContainerBottomMargin};
	}
	
	label {
		${visuallyHiddenLabelStyle}
	}
`;


// Define the accepted props
interface Props {
	pageID: number;
	onChange: () => void;
	storybookActivePanel?: 'add' | 'manage';
	storybookShowPanel?: true;
	storybookMyPhotos?: GetMyPhotosResponse;
}


// Function component
const Upsert: React.FC<Props> = ({ pageID, onChange, storybookActivePanel, storybookShowPanel, storybookMyPhotos }) => {
	// Use state
	const [activePanel, setActivePanel] = useState<'add' | 'manage' | null>(storybookActivePanel ?? null);
	const [showPanel, setShowPanel] = useState(storybookShowPanel ?? false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use ref
	const fileGetter = useRef<(() => FileList | null) | null>(null);
	const fileSetter = useRef<((files: FileList | null) => void) | null>(null);
	const fileRef = useRef<HTMLInputElement>(null);
	
	
	// Function that handles closing the panel and resetting the form
	const closePanelAndResetForm = () => {
		// Close panel
		setShowPanel(false);
		
		
		// Reset file input
		fileSetter.current?.(null);
		
		
		// Clear the underlying input element's value
		if (fileRef.current) {
			fileRef.current.value = '';
		}
	};
	
	
	// Build form options
	const formOptions: APIFormOptions<PostMyPhotosFiles, undefined, undefined> = {
		action: 'POST',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/type/206/my-photos`,
		submitButton: {
			text: 'Upload',
		},
		cancelButton: {
			text: 'Cancel',
		},
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		// Initialize
		const fileCount = fileGetter.current?.()?.length ?? 0;
		
		
		// Display page message
		dispatch(
			commonActions.showPageMessage({
				title: 'Success!',
				message: `Your photo${fileCount > 1 ? 's' : ''} ha${fileCount > 1 ? 've' : 's'} been added.`,
				color: 'success',
			})
		);
		
		
		// Close panel and reset form
		closePanelAndResetForm();
		
		
		// Reload photo gallery
		onChange();
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			<ButtonGroup>
				<UserControlButton
					text='Add your photo(s)'
					opened={showPanel && activePanel === 'add'}
					onClick={() => {
						setActivePanel('add');
						setShowPanel(true);
					}}
					icon={faPlus}
				/>
				<UserControlButton
					text='Manage photos you’ve uploaded'
					opened={showPanel && activePanel === 'manage'}
					onClick={() => {
						setActivePanel('manage');
						setShowPanel(true);
					}}
					icon={faPencil}
					iconSize='sm'
				/>
			</ButtonGroup>
			
			<AnimateHeight height={showPanel ? 'auto' : 0} duration={400} animateOpacity>
				<Panel
					closeOnClick={closePanelAndResetForm}
					signInText={activePanel === 'add' ? 'adding photos' : 'managing your photos'}
					showClose={activePanel === 'manage'}
				>
					{activePanel === 'add' && (
						<APIForm options={formOptions} onCancel={closePanelAndResetForm} onCompletion={handleFormCompletion}>
							<FileFieldContainer>
								<File
									name='photos'
									label='Photos'
									accept='image/*'
									multiple
									bindGetter={(getter) => (fileGetter.current = getter)}
									bindSetter={(setter) => (fileSetter.current = setter)}
									ref={fileRef}
								/>
							</FileFieldContainer>
						</APIForm>
					)}
					
					{activePanel === 'manage' && (
						<PhotoGalleryTable onChange={onChange} pageID={pageID} storybookMyPhotos={storybookMyPhotos} />
					)}
				</Panel>
			</AnimateHeight>
		</React.Fragment>
	);
};

export default Upsert;
